import { useEffect, useState } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import {
  DollarSign,
  CheckCircle,
  Clock,
  FolderOpen,
  Building2,
  Tag,
  Layers
} from 'lucide-react';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function Dashboard() {
  const [stats, setStats] = useState({
    totalExpenses: 0,
    totalExpensesChange: '',
    pending: 0,
    approved: 0,
    totalValue: 0,
    categoryStats: [],
    departmentStats: []
  });

  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/dashboard/summary')
      .then(res => {
        const data = res?.data || {};
        // If API returns a different shape (e.g., { kpis: {...} }), keep defaults and only merge known keys
        const safe = {
          totalExpenses: Number(data.totalExpenses ?? data.total_expenses ?? stats.totalExpenses ?? 0),
          totalExpensesChange: data.totalExpensesChange ?? data.total_expenses_change ?? stats.totalExpensesChange ?? '',
          pending: Number(data.pending ?? data.pending_count ?? stats.pending ?? 0),
          approved: Number(data.approved ?? data.approved_count ?? stats.approved ?? 0),
          totalValue: Number(data.totalValue ?? data.total_value ?? stats.totalValue ?? 0),
          categoryStats: Array.isArray(data.categoryStats || data.category_stats) ? (data.categoryStats || data.category_stats) : (stats.categoryStats || []),
          departmentStats: Array.isArray(data.departmentStats || data.department_stats) ? (data.departmentStats || data.department_stats) : (stats.departmentStats || [])
        };
        setStats(safe);
      })
      .catch(err => console.error('Dashboard data fetch error:', err));
  }, []);

  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue('--text')?.trim() || '#111827';
  const mutedColor = css.getPropertyValue('--muted')?.trim() || '#6b7280';
  const panelColor = css.getPropertyValue('--panel')?.trim() || '#ffffff';
  const bgColor = css.getPropertyValue('--bg')?.trim() || '#f7f7fb';

  // Keep original dashboard palette (requested)
  const palette = [
    '#EDE8D0', '#CAB7A1', '#FFB6C1', '#B29576', '#8B4513',
    '#b6524fff', '#df6767ff', '#f87171', '#811112', '#430606ff'
  ];

  // Doughnut data for Category
  const doughnutData = (data, label) => ({
    labels: data.map(item => item.category || item.department),
    datasets: [{
      label,
      data: data.map(item => item.total_amount),
      backgroundColor: palette,
      borderColor: bgColor,
      borderWidth: 2,
      hoverOffset: 8,
    }]
  });

  // Horizontal bar for Department
  const barData = (data, label) => ({
   labels: data.map(item => item.department || item.category),
   datasets: [{
     label,
     data: data.map(item => item.total_amount),
     backgroundColor: palette,
     borderColor: bgColor,
     borderWidth: 1.5,
     borderRadius: 8,
     barThickness: 16,
   }]
  });

  // theme-driven chart UI

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: { boxWidth: 12, font: { size: 12 }, color: textColor }
      },
      tooltip: {
        titleColor: textColor,
        bodyColor: textColor,
        backgroundColor: panelColor,
        borderColor: mutedColor,
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            let label = context.label || '';
            if (label) label += ': ';
            if (context.parsed !== null) {
              label += new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR'
              }).format(context.parsed);
            }
            return label;
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {
        ticks: { color: textColor },
        grid: { color: 'rgba(148,163,184,0.25)' }
      },
      y: {
        ticks: { color: textColor },
        grid: { display: false }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        titleColor: textColor,
        bodyColor: textColor,
        backgroundColor: panelColor,
        borderColor: mutedColor,
        borderWidth: 1,
        callbacks: {
          label: (context) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(context.parsed.x || context.parsed)
        }
      }
    }
  };

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>Dashboard Overview</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>High-level metrics, pending approvals, and expenditure trends</p>
      </div>

      {/* Total Value Card */}
      <div
        className="text-white rounded-2xl shadow-lg p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between"
        style={{
          background: `linear-gradient(90deg, var(--card-gradient-start) 0%, var(--card-gradient-mid) 50%, var(--card-gradient-end) 100%)`
        }}
      >
        <div>
          <div className="text-lg uppercase tracking-wider font-semibold mb-1">Total Expense Value</div>
          <div className="text-4xl sm:text-5xl font-bold">₹{Number(stats.totalValue || 0).toLocaleString('en-IN')}</div>
          <div className="text-sm opacity-80 mt-1">{stats.totalValueChange || '+% from last month'}</div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div onClick={() => navigate('/expenses')} className="hover:shadow-md transition-shadow rounded-xl p-5 border flex items-center gap-4 cursor-pointer" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <FolderOpen className="w-10 h-10" style={{ color: 'var(--accent)' }} />
          <div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Total Expenses</div>
            <div className="text-2xl font-bold">{stats.totalExpenses}</div>
          </div>
        </div>

        <div onClick={() => navigate('/pending-approval-list')} className="hover:shadow-md transition-shadow rounded-xl p-5 border flex items-center gap-4 cursor-pointer" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <Clock className="w-10 h-10" style={{ color: '#f59e0b' }} />
          <div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Pending Approvals</div>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </div>
        </div>

        <div onClick={() => navigate('/approved-list')} className="hover:shadow-md transition-shadow rounded-xl p-5 border flex items-center gap-4 cursor-pointer" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <CheckCircle className="w-10 h-10" style={{ color: '#22c55e' }} />
          <div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Approved</div>
            <div className="text-2xl font-bold">{stats.approved}</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Category Doughnut Chart */}
        <div className="rounded-xl shadow-sm border p-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text)' }}>
            <Tag className="w-8 h-8" style={{ color: 'var(--muted)' }} />
            By Category
          </h3>
          {(stats.categoryStats?.length || 0) > 0 ? (
            <div className="h-[300px] w-full relative">
              <Doughnut data={doughnutData(stats.categoryStats, 'Category')} options={doughnutOptions} />
            </div>
          ) : (
            <p style={{ color: 'var(--muted)' }}>No category data available.</p>
          )}
        </div>

        {/* Department Horizontal Bar Chart */}
        <div className="rounded-xl shadow-sm border p-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text)' }}>
            <Building2 className="w-8 h-8" style={{ color: 'var(--accent)' }} />
            By Department
          </h3>
          {(stats.departmentStats?.length || 0) > 0 ? (
            <div className="h-[300px] w-full relative">
              <Bar data={barData(stats.departmentStats, 'Department')} options={barOptions} />
            </div>
          ) : (
            <p style={{ color: 'var(--muted)' }}>No department data available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;





// import { useEffect, useState } from 'react';
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';
// import { Pie } from 'react-chartjs-2';
// import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// ChartJS.register(ArcElement, Tooltip, Legend);

// function Dashboard() {
//   const [stats, setStats] = useState({
//     totalExpenses: 0,
//     totalExpensesChange: '',
//     pending: 0,
//     approved: 0,
//     totalValue: 0,
//     categoryStats: [],
//     departmentStats: []
//   });

//   const navigate = useNavigate();

//   useEffect(() => {
//     axios.get('http://localhost:5000/api/dashboard/summary')
//       .then(res => setStats(res.data))
//       .catch(err => console.error('Dashboard data fetch error:', err));
//   }, []);

//   const pieData = (data, label) => ({
//     labels: data.map(item => item.category || item.department),
//     datasets: [{
//       label,
//       data: data.map(item => item.total_amount),
//       backgroundColor: ['#f87171', '#60a5fa', '#34d399', '#facc15', '#a78bfa', '#fb923c', '#f472b6', '#38bdf8', '#4ade80', '#e879f9'],
//       borderColor: ['#f87171', '#60a5fa', '#34d399', '#facc15', '#a78bfa', '#fb923c', '#f472b6', '#38bdf8', '#4ade80', '#e879f9'],
//       borderWidth: 1,
//     }]
//   });

//   const pieOptions = {
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: {
//         position: 'top',
//         labels: {
//           boxWidth: 14,
//           font: { size: 12 }
//         }
//       },
//       tooltip: {
//         callbacks: {
//           label: function (context) {
//             let label = context.label || '';
//             if (label) label += ': ';
//             if (context.parsed !== null) {
//               label += new Intl.NumberFormat('en-IN', {
//                 style: 'currency',
//                 currency: 'INR'
//               }).format(context.parsed);
//             }
//             return label;
//           }
//         }
//       }
//     }
//   };

//   return (
//     <div className="p-6 w-full max-w-screen-xl mx-auto font-sans">
//       <h2 className="text-3xl font-bold mb-8 text-gray-800">Dashboard</h2>

//       <div className="flex flex-wrap justify-center gap-6 mb-12">
//         <div onClick={() => navigate('/expenses')} className="cursor-pointer flex-1 min-w-[240px] max-w-[300px] bg-gradient-to-br from-blue-100 to-blue-200 shadow-lg rounded-xl p-6 border border-blue-300 hover:scale-105 transform transition">
//           <div className="text-base text-blue-800 font-semibold mb-1">Total Expenses</div>
//           <div className="text-3xl font-extrabold text-blue-900">{stats.totalExpenses}</div>
//           <div className="text-sm text-blue-700 mt-2">{stats.totalExpensesChange || '+0 from last month'}</div>
//         </div>

//         <div onClick={() => navigate('/pending-approval-list')} className="cursor-pointer flex-1 min-w-[240px] max-w-[300px] bg-gradient-to-br from-yellow-100 to-yellow-200 shadow-lg rounded-xl p-6 border border-yellow-300 hover:scale-105 transform transition">
//           <div className="text-base text-yellow-800 font-semibold mb-1">Pending Approvals</div>
//           <div className="text-3xl font-extrabold text-yellow-900">{stats.pending}</div>
//           <div className="text-sm text-yellow-700 mt-2">Awaiting reviews</div>
//         </div>

//         <div onClick={() => navigate('/approved-list')} className="cursor-pointer flex-1 min-w-[240px] max-w-[300px] bg-gradient-to-br from-green-100 to-green-200 shadow-lg rounded-xl p-6 border border-green-300 hover:scale-105 transform transition">
//           <div className="text-base text-green-800 font-semibold mb-1">Approved</div>
//           <div className="text-3xl font-extrabold text-green-900">{stats.approved}</div>
//           <div className="text-sm text-green-700 mt-2">This month</div>
//         </div>

//         <div className="flex-1 min-w-[240px] max-w-[300px] bg-gradient-to-br from-purple-100 to-purple-200 shadow-lg rounded-xl p-6 border border-purple-300">
//           <div className="text-base text-purple-800 font-semibold mb-1">Total Value</div>
//           <div className="text-3xl font-extrabold text-purple-900">₹{stats.totalValue.toLocaleString('en-IN')}</div>
//           <div className="text-sm text-purple-700 mt-2">{stats.totalValueChange || '+% from last month'}</div>
//         </div>
//       </div>

//       <div className="flex flex-wrap justify-center gap-8">
//         <div className="w-full md:w-5/12 bg-white shadow-xl rounded-xl p-8 border border-gray-200 flex flex-col items-center">
//           <h4 className="text-xl font-bold mb-6 text-gray-700">Expense Distribution by Category</h4>
//           {stats.categoryStats?.length > 0 ? (
//             <div className="relative h-[300px] w-full">
//               <Pie data={pieData(stats.categoryStats, 'Category')} options={pieOptions} />
//             </div>
//           ) : <p className="text-gray-400 text-base">No data available for categories.</p>}
//         </div>

//         <div className="w-full md:w-5/12 bg-white shadow-xl rounded-xl p-8 border border-gray-200 flex flex-col items-center">
//           <h4 className="text-xl font-bold mb-6 text-gray-700">Expense Distribution by Department</h4>
//           {stats.departmentStats?.length > 0 ? (
//             <div className="relative h-[300px] w-full">
//               <Pie data={pieData(stats.departmentStats, 'Department')} options={pieOptions} />
//             </div>
//           ) : <p className="text-gray-400 text-base">No data available for departments.</p>}
//         </div>
//       </div>
//     </div>
//   );
// }

// export default Dashboard;





